<script lang="ts" setup>
import { nextTick, ref, watch } from 'vue'
import { v4 as uuid } from 'uuid'
import { resources } from '@/config/brands'

const props = defineProps<{
  loading: boolean
  config: any
  activeTab: number
  tagsList: Array<{ id: string; isHot: boolean; tagName: string }>
  activeContentTab: number
  contentTabs: string[]
  filteredBooks: any[]
  tabLoading: boolean
  ending: boolean
  fetching: boolean
  booksData: any[]
  infiniteScrollProps: Record<string, any>
  setContentContainer: (el: HTMLElement | null) => void
  gotoBook: (id: string) => void
}>()

const emit = defineEmits<{
  (event: 'update:active-tab', value: number): void
  (event: 'update:active-content-tab', value: number): void
}>()

const tagListContainer = ref<HTMLElement | null>(null)

watch(
  () => props.activeTab,
  async () => {
    await nextTick()
    scrollToActiveTag()
  },
  { immediate: true },
)

function scrollToActiveTag() {
  if (!tagListContainer.value)
    return

  const activeButton = tagListContainer.value.querySelectorAll('button')[props.activeTab] as HTMLElement | undefined
  activeButton?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  })
}

function handleTagClick(index: number) {
  if (props.activeTab === index)
    return
  emit('update:active-tab', index)
}
</script>

<template>
  <div v-if="loading" class="py-120px flex items-center justify-center space-x-4 text-13px text-n-text-3">
    <div class="animate-spin">
      <i-icon-park-outline-loading />
    </div>
    <p>{{ config.texts.loading }}</p>
  </div>
  <div v-else class="h-100dvh box-border bg-white overflow-hidden pt-40px">
    <BasicNavbar :scale="true" class="color-[rgba(50,53,58,1)] bg-white">
      <template #default>
        <div class="absolute inset-0 flex justify-center items-center pointer-events-none">
          <p class="text-18px leading-16px font-medium text-[rgba(50,53,58,1)]">
            {{ config.texts.allTags }}
          </p>
        </div>
      </template>
    </BasicNavbar>

    <div id="tagsContainer" class="h-full flex overflow-hidden">
      <div
        ref="tagListContainer"
        class="w-80px flex-shrink-0 bg-[rgba(242,245,248,1)] overflow-y-auto overflow-x-hidden"
      >
        <button
          v-for="(tag, index) in tagsList"
          :key="tag.id || index"
          class="relative w-full min-h-54px px-6px py-17px text-10px leading-12px text-center"
          :class="activeTab === index ? 'bg-white text-[rgba(50,53,58,1)]' : 'text-[rgba(150,156,170,1)]'"
          @click="handleTagClick(index)"
        >
          <span
            v-if="activeTab === index"
            class="absolute left-0 top-1/2 h-24px w-3px -translate-y-1/2 rounded-r-2px bg-[rgba(68,150,255,1)]"
          />
          <!-- 把图标作为内联元素处理，使其与文字同行并随文字换行 -->
          <span class="inline-block max-w-full text-center">
            <img
              v-if="tag.isHot"
              :src="resources?.tagsIcons?.hotIconRed"
              class="inline-block align-middle mr-2px w-8px h-9px"
              alt="火花图标"
            >
            <span class="break-words whitespace-normal inline">{{ tag.tagName }}</span>
          </span>
        </button>
      </div>

      <div class="min-w-0 flex-1 flex flex-col bg-white px-7px pt-8px">
        <div class="px-5px pb-7px">
          <div class="flex w-full rounded-18px bg-[rgba(239,240,245,1)] p-0">
            <button
              v-for="(tab, index) in contentTabs"
              :key="index"
              class="flex h-32px flex-1 items-center justify-center rounded-18px px-20px text-14px leading-14px"
              :class="activeContentTab === index ? 'bg-[rgba(68,150,255,1)] text-white' : 'text-[rgba(106,110,122,1)]'"
              @click="emit('update:active-content-tab', index)"
            >
              {{ tab }}
            </button>
          </div>
        </div>

        <div :ref="setContentContainer" v-bind="infiniteScrollProps" class="flex-1 overflow-y-auto px-5px pb-8px">
          <div v-if="tabLoading" class="py-120px flex items-center justify-center space-x-4 text-13px text-[rgba(150,156,170,1)]">
            <div class="animate-spin">
              <i-icon-park-outline-loading />
            </div>
            <p>{{ config.texts.loading }}</p>
          </div>

          <template v-else>
            <div class="space-y-6px">
              <button
                v-for="(item, index) in filteredBooks"
                :key="index"
                class="w-full flex items-start gap-20px py-6px text-left"
                @click="gotoBook(item.id)"
              >
                <BizBookCover
                  :id="uuid()"
                  :src="item.contentCoverUrl || ''"
                  :extra="{
                    author: item?.authorInfo?.author,
                    module_name: 'tags',
                    ...item,
                  }"
                  class="w-50px h-66px rounded-8px flex-shrink-0 overflow-hidden"
                />

                <div class="min-w-0 flex-1 pt-7px">
                  <p class="text-12px leading-14px font-medium line-clamp-1 text-[rgba(50,53,58,1)]">
                    {{ item.contentName }}
                  </p>
                  <p class="mt-4px text-10px leading-16px line-clamp-1 text-[rgba(150,156,170,1)]">
                    {{ item.introduce }}
                  </p>
                  <div class="mt-8px flex items-center text-10px leading-10px text-[rgba(255,122,61,1)]">
                    <i-icon-park-outline-preview-open class="mr-4px h-12px w-12px flex-shrink-0" />
                    <span>{{ item.viewUsers || 0 }}</span>
                  </div>
                </div>
              </button>
            </div>

            <div v-if="ending && !fetching" class="py-15px text-13px text-[rgba(150,156,170,1)] text-center">
              {{ config.texts.noMore }}
            </div>
            <div
              v-if="fetching && booksData.length > 0"
              class="py-15px flex items-center justify-center space-x-4 text-13px text-[rgba(150,156,170,1)]"
            >
              <div class="animate-spin">
                <i-icon-park-outline-loading />
              </div>
              <p>{{ config.texts.loading }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
